<template>
  <div class="card p-4 shadow-sm">
    <!-- Loader pantalla completa mientras se genera la factura FEL -->
    <div
      v-if="(cargando && tipoFacturacion === 'con_factura') || esperandoImpresion"
      style="position:fixed;inset:0;background:rgba(0,0,0,0.72);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px"
    >
      <div class="spinner-border text-light" style="width:3.5rem;height:3.5rem"></div>
      <div class="text-white fw-semibold fs-5">Generando factura electrónica...</div>
      <div class="text-white-50 small">Por favor espere, procesando con la SAT</div>
    </div>
    <div class="text-center mb-4">
      <h3 class="m-0">Venta de Servicios Extra</h3>
    </div>

    <!-- Buscador -->
    <div class="mb-3">
      <input
        type="text"
        class="form-control"
        placeholder="Buscar servicio por nombre"
        v-model="busqueda"
        />
    </div>

    <!-- Servicios -->
    <div class="mb-4" style="max-height:220px;overflow-y:auto">
      <div v-if="serviciosFiltrados.length" class="row g-2">
        <div class="col-md-3" v-for="serv in serviciosFiltrados" :key="serv.IdServicio">
          <div class="border p-2 rounded d-flex justify-content-between align-items-center">
            <span>{{ serv.Nombre }} (Q{{ Number(serv.precio).toFixed(2) }})</span>
            <button class="btn btn-sm btn-outline-primary" @click="agregarServicio(serv)">Agregar</button>
          </div>
        </div>
      </div>
      <p v-else-if="todosLosServicios.length === 0" class="text-muted text-center small py-2">Cargando servicios...</p>
      <p v-else class="text-muted text-center small py-2">Sin resultados para "{{ busqueda }}"</p>
    </div>

    <!-- Carrito -->
    <div class="table-responsive mb-4">
      <table class="table table-bordered align-middle text-center">
        <thead class="table-light">
          <tr>
            <th>Servicio</th>
            <th>Precio (Q)</th>
            <th>Cantidad</th>
            <th>Total (Q)</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in carrito" :key="item.IdServicio">
            <td>{{ item.Nombre }}</td>
            <td>Q{{ Number(item.precio).toFixed(2) }}</td>
            <td>
              <input type="number" v-model.number="item.cantidad" class="form-control form-control-sm" min="1" />
            </td>
            <td>Q{{ (item.cantidad * item.precio).toFixed(2) }}</td>
            <td>
              <button class="btn btn-sm btn-danger" @click="eliminarServicio(index)">Eliminar</button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th colspan="3" class="text-end">Total Venta:</th>
            <th colspan="2">Q{{ totalVenta.toFixed(2) }}</th>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Tipo de venta -->
    <div class="mb-4">
      <label class="form-label fw-bold">Tipo de Venta</label>
      <select
        v-model="tipoVenta"
        class="form-select"
        :disabled="carrito.length === 0"
      >
        <option :value="null" disabled>Seleccione tipo de venta</option>
        <option value="pagar_ahora">Pagar Ahora</option>
        <option value="cargar_reserva">Cargar a Reserva</option>
      </select>
      <small v-if="carrito.length === 0" class="text-muted">
        Agregue al menos un servicio para continuar
      </small>
    </div>

    <!-- Buscador de reservas (solo si Cargar a Reserva) -->
    <div v-if="tipoVenta === 'cargar_reserva'" class="mb-4">
      <label class="form-label fw-bold">Buscar Reserva</label>
      <input
        type="text"
        v-model="busquedaReserva"
        class="form-control"
        placeholder="Código de reserva"
        @input="debouncedBuscarReservas"
      />

      <!-- Lista de reservas encontradas -->
      <ul class="list-group mt-2" v-if="Array.isArray(reservasFiltradas) && reservasFiltradas.length">
        <li
          v-for="res in reservasFiltradas"
          :key="res.IdReserva"
          class="list-group-item"
        >
          <div>
            <strong>R00{{ res.IdReserva }}</strong> -
            {{ res.huesped?.Nombre }} {{ res.huesped?.Apellido }}
            <span class="text-muted">({{ estadoLegible(res.EstadoReserva) }})</span> |
            📞 {{ res.huesped?.Telefono }} |
            🗓️ {{ formatearFecha(res.FechaLlegada) }} - {{ formatearFecha(res.FechaSalida) }}
          </div>

          <div v-if="res.habitaciones_info?.length" class="mt-2 ms-3">
            <ul class="list-group">
              <li
                v-for="hab in res.habitaciones_info"
                :key="hab.IdReservaHabitacion"
                class="list-group-item list-group-item-action"
                :class="{ 'active': habitacionSeleccionada?.IdReservaHabitacion === hab.IdReservaHabitacion }"
                @click="seleccionarHabitacion(res, hab)"
                style="cursor: pointer;"
              >
                🛏️ Habitación {{ hab.Numero }}
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <!-- Habitación seleccionada -->
      <div v-if="habitacionSeleccionada" class="alert alert-info mt-3">
        <strong>Seleccionado:</strong> R00{{ reservaSeleccionada.IdReserva }} - Habitación {{ habitacionSeleccionada.Numero }}
        <button class="btn btn-sm btn-outline-secondary ms-2" @click="limpiarSeleccionReserva">Cambiar</button>
      </div>
    </div>

    <!-- Botón Confirmar (solo para Cargar a Reserva) -->
    <div class="text-end" v-if="tipoVenta === 'cargar_reserva'">
      <button
        class="btn btn-success"
        @click="iniciarVenta"
        :disabled="!puedeIniciarVenta"
      >
        💳 Confirmar Venta
      </button>
    </div>

    <!-- Modal Pregunta Factura -->
    <div
      v-if="mostrarModalFactura"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🧾 Tipo de Facturación</h5>
            <button class="btn-close" @click="cerrarModalFactura"></button>
          </div>
          <div class="modal-body text-center">
            <p class="mb-4">¿Desea generar factura con NIT o CF?</p>
            <div class="d-flex justify-content-center gap-3">
              <button class="btn btn-primary" @click="seleccionarTipoFactura('con_factura')">
                ✅ Sí, con Factura
              </button>
              <button class="btn btn-secondary" @click="seleccionarTipoFactura('sin_factura')">
                ❌ No, sin Factura
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Pago -->
    <div
      v-if="mostrarModalPago"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">💳 Registrar Pago</h5>
            <button class="btn-close" @click="cerrarModalPago"></button>
          </div>
          <div class="modal-body">
            <!-- Info del total -->
            <div class="alert alert-info d-flex justify-content-between align-items-center">
              <span><strong>Total a Pagar:</strong> Q{{ totalVenta.toFixed(2) }}</span>
              <span :class="{'text-success': totalPagado >= totalVenta, 'text-danger': totalPagado < totalVenta}">
                <strong>Pagado:</strong> Q{{ totalPagado.toFixed(2) }}
                <span v-if="totalPagado < totalVenta"> (Faltan: Q{{ (totalVenta - totalPagado).toFixed(2) }})</span>
              </span>
            </div>

            <!-- Cambio -->
            <div v-if="cambioVenta > 0" class="alert alert-warning py-2 mb-2">
              <div class="d-flex justify-content-between">
                <span>💵 Efectivo recibido:</span>
                <strong>Q{{ totalPagado.toFixed(2) }}</strong>
              </div>
              <div class="d-flex justify-content-between border-top pt-1 mt-1">
                <span>➡️ <strong>CAMBIO:</strong></span>
                <strong class="fs-5">Q{{ cambioVenta.toFixed(2) }}</strong>
              </div>
            </div>

            <!-- Otros servicios (solo si es con factura) -->
            <div v-if="tipoFacturacion === 'con_factura'" class="mb-3 form-check">
              <input
                id="checkOtroServicioVenta"
                v-model="otroServicio"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="checkOtroServicioVenta">
                Facturar como Otros servicios (solo IVA)
              </label>
            </div>

            <!-- Selección NIT o CF (solo si es con factura) -->
            <div v-if="tipoFacturacion === 'con_factura'" class="mb-3">
              <label class="form-label fw-semibold">Tipo de Factura</label>
              <div class="d-flex gap-3">
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="tipo_nit" value="nit" v-model="subTipoFactura">
                  <label class="form-check-label" for="tipo_nit">Factura con NIT</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="tipo_cf" value="cf" v-model="subTipoFactura">
                  <label class="form-check-label" for="tipo_cf">Consumidor Final (CF)</label>
                </div>
              </div>
            </div>

            <!-- Campo NIT si aplica -->
            <div v-if="tipoFacturacion === 'con_factura' && subTipoFactura === 'nit'" class="mb-3">
              <label class="form-label fw-semibold">NIT del Cliente</label>
              <div class="input-group">
                <input type="text" v-model="nitCliente" @input="nitVerificado = false" class="form-control" placeholder="Ingrese el NIT" />
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  @click="verificarNitManual"
                  :disabled="cargandoVerificacionNit || !nitCliente.trim()"
                >
                  <span v-if="cargandoVerificacionNit" class="spinner-border spinner-border-sm"></span>
                  <span v-else>Verificar</span>
                </button>
              </div>
              <small v-if="nitVerificado" class="text-success"><i class="bi bi-check-circle-fill me-1"></i>NIT verificado</small>
              <small v-else-if="nitCliente.trim()" class="text-warning"><i class="bi bi-exclamation-triangle me-1"></i>Debes verificar el NIT antes de pagar</small>
            </div>

            <!-- Direccion opcional (solo cuando se genera factura) -->
            <div v-if="tipoFacturacion === 'con_factura'" class="mb-3">
              <label class="form-label fw-semibold">Dirección (opcional)</label>
              <input
                type="text"
                v-model="direccionReceptor"
                class="form-control"
                placeholder="Ciudad"
              />
            </div>

            <!-- Lista de pagos -->
            <div class="mb-3">
              <label class="form-label fw-semibold">Métodos de Pago</label>

              <div v-for="(pago, index) in pagos" :key="index" class="row g-2 mb-2 align-items-center">
                <div class="col-md-3">
                  <select v-model="pago.IdMetodoPago" class="form-select" @change="onMetodoPagoChange(index)">
                    <option disabled value="">Seleccione método</option>
                    <option v-for="m in metodosPago" :key="m.IdMetodoPago" :value="m.IdMetodoPago">
                      {{ m.Metodo }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <input
                    type="number"
                    v-model.number="pago.Monto"
                    min="0.01"
                    step="0.01"
                    class="form-control"
                    placeholder="Monto Q"
                    :disabled="!pago.IdMetodoPago"
                  />
                </div>

                <!-- Cuenta bancaria (solo transferencia = 2) -->
                <div class="col-md-3" v-if="pago.IdMetodoPago == 2">
                  <select v-model="pago.IdCuentaBancaria" class="form-select">
                    <option disabled value="">Cuenta bancaria</option>
                    <option v-for="cuenta in cuentasBancarias" :key="cuenta.IdCuentaBancaria" :value="cuenta.IdCuentaBancaria">
                      {{ cuenta.Banco }} - {{ cuenta.NumeroCuenta }}
                    </option>
                  </select>
                </div>

                <!-- Referencia -->
                <div class="col-md-3" v-if="pago.IdMetodoPago == 2 || pago.IdMetodoPago == 3 || pago.IdMetodoPago == 4">
                  <input type="text" v-model="pago.NumeroReferencia" class="form-control" placeholder="Referencia" />
                </div>

                <div class="col-md-1">
                  <button
                    class="btn btn-danger btn-sm"
                    @click="removerPago(index)"
                    :disabled="pagos.length === 1"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <button class="btn btn-outline-success btn-sm" @click="agregarPago">
                + Agregar Método de Pago
              </button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalPago">Cancelar</button>
            <button
              class="btn btn-success"
              @click="finalizarVenta"
              :disabled="!puedeFinalizarPago"
            >
              <span v-if="cargando" class="spinner-border spinner-border-sm me-2"></span>
              Confirmar Pago
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="mostrarToast" class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
      <div class="toast show text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '@/config/config.js';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

export default {
  name: 'VentaServiciosExtra',
  data() {
    return {
      busqueda: '',
      carrito: [],
      tipoVenta: null,
      tipoFacturacion: null, // 'con_factura' o 'sin_factura'
      subTipoFactura: null, // 'nit' o 'cf'
      nitCliente: '',
      nombreCliente: '',
      direccionReceptor: '',
      metodosPago: [],
      pagos: [{ IdMetodoPago: '', Monto: '', NumeroReferencia: '', IdCuentaBancaria: '' }],
      reservasFiltradas: [],
      todosLosServicios: [],
      busquedaReserva: '',
      reservaSeleccionada: null,
      habitacionSeleccionada: null,
      debouncedBuscarReservas: null,
      alerta: { mensaje: '', tipo: 'success' },
      mostrarToast: false,
      cuentasBancarias: [],
      // Modales
      mostrarModalFactura: false,
      mostrarModalPago: false,
      cargando: false,
      nitVerificado: false,
      cargandoVerificacionNit: false,
      otroServicio: false,
      esperandoImpresion: false,
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    totalVenta() {
      return this.carrito.reduce((total, item) => total + Number(item.precio) * item.cantidad, 0);
    },
    totalPagado() {
      return this.pagos.reduce((total, p) => total + (Number(p.Monto) || 0), 0);
    },
    puedeIniciarVenta() {
      if (!this.carrito.length) return false;
      if (!this.tipoVenta) return false;
      if (this.tipoVenta === 'cargar_reserva' && !this.habitacionSeleccionada) return false;
      return true;
    },
    puedeFinalizarPago() {
      if (this.totalPagado < this.totalVenta) return false;
      if (!this.pagos.some(p => p.IdMetodoPago && p.Monto > 0)) return false;
      const pagosActivos = this.pagos.filter(p => p.IdMetodoPago && p.Monto > 0);
      if (pagosActivos.some(p => this.requiereReferencia(p.IdMetodoPago) && !String(p.NumeroReferencia).trim())) return false;
      if (this.tipoFacturacion === 'con_factura') {
        if (!this.subTipoFactura) return false;
        if (this.subTipoFactura === 'nit' && !this.nitCliente.trim()) return false;
        if (this.subTipoFactura === 'nit' && !this.nitVerificado) return false;
      }
      return true;
    },
    toastColor() {
      return {
        success: 'bg-success',
        danger: 'bg-danger',
        warning: 'bg-warning text-dark',
        info: 'bg-info text-dark'
      }[this.alerta.tipo] || 'bg-primary';
    },
    iconoToast() {
      return {
        success: 'bi bi-check-circle-fill',
        danger: 'bi bi-x-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }[this.alerta.tipo] || 'bi bi-info-circle-fill';
    },
    facturadoValue() {
      return this.tipoFacturacion === 'sin_factura' ? 2 : 1;
    },
    cambioVenta() {
      return Math.max(0, this.totalPagado - this.totalVenta);
    },
    serviciosFiltrados() {
      const q = this.busqueda?.trim().toLowerCase();
      if (!q) return this.todosLosServicios;
      return this.todosLosServicios.filter(s => s.Nombre?.toLowerCase().includes(q));
    }
  },
  watch: {
    tipoVenta(newVal) {
      if (newVal === 'pagar_ahora') {
        this.limpiarSeleccionReserva();
        // Mostrar modal de facturación automáticamente si hay items en el carrito
        if (this.carrito.length > 0) {
          this.mostrarModalFactura = true;
        }
      } else if (newVal === 'cargar_reserva') {
        this.tipoFacturacion = null;
        this.nitCliente = '';
        this.nombreCliente = '';
        this.pagos = [{ IdMetodoPago: '', Monto: '', NumeroReferencia: '', IdCuentaBancaria: '' }];
      }
    },
    async subTipoFactura(val) {
      this.nitVerificado = false;
      if (val === 'nit') {
        // Auto-fill NIT del huésped si hay reserva/habitación seleccionada
        const nit = this.reservaSeleccionada?.huesped?.NIT
          || this.habitacionSeleccionada?.reserva?.huesped?.NIT
          || '';
        if (nit) {
          this.nitCliente = nit;
          try {
            const { data } = await axios.get(`${API_URL}/facturas-fel/verificar-nit/${encodeURIComponent(nit)}`);
            if (data.nombre) this.nombreCliente = data.nombre;
            this.nitVerificado = !!data.valido;
          } catch { /* ignorar error de validación */ }
        }
      } else if (val === 'cf') {
        this.nitCliente = '';
        this.nombreCliente = '';
      }
    }
  },
  methods: {
    async verificarNitManual() {
      if (!this.nitCliente.trim()) return;
      this.cargandoVerificacionNit = true;
      this.nitVerificado = false;
      try {
        const { data } = await axios.get(
          `${API_URL}/facturas-fel/verificar-nit/${encodeURIComponent(this.nitCliente.trim())}`,
        );
        if (data.valido) {
          this.nitVerificado = true;
          if (data.nombre) this.nombreCliente = data.nombre;
          this.mostrarToastMensaje('NIT verificado correctamente', 'success');
        } else {
          this.mostrarToastMensaje('NIT no válido o no encontrado', 'danger');
        }
      } catch {
        this.mostrarToastMensaje('Error al verificar el NIT', 'danger');
      } finally {
        this.cargandoVerificacionNit = false;
      }
    },
    imprimirTicketTermica(uuid, cambio = 0) {
      const params = cambio > 0 ? `?cambio=${cambio}` : '';
      const url = `${API_URL}/facturas-fel/${uuid}/ticket${params}`;
      const win = window.open(url, '_blank');
      if (win) setTimeout(() => { this.esperandoImpresion = false; win.print(); }, 1500);
      else this.esperandoImpresion = false;
    },
    async cargarTodosLosServicios() {
      try {
        const { data } = await axios.get(`${API_URL}/servicios-extra`);
        this.todosLosServicios = (Array.isArray(data) ? data : []).map(s => ({ ...s, precio: Number(s.precio || s.Precio) }));
      } catch (error) {
        console.error('Error cargando servicios:', error);
      }
    },
    async obtenerCuentasBancarias() {
      try {
        const res = await axios.get(`${API_URL}/cuentas-bancarias`);
        this.cuentasBancarias = res.data;
      } catch (error) {
        console.error('Error al obtener cuentas bancarias:', error);
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return '';
      const date = new Date(fecha);
      return date.toISOString().split('T')[0];
    },
    estadoLegible(estado) {
      return ['Pendiente', 'En curso', 'Finalizada', 'Cancelada'][estado] || 'Desconocido';
    },
    agregarServicio(serv) {
      if (!this.carrito.find(s => s.IdServicio === serv.IdServicio)) {
        this.carrito.push({ ...serv, precio: Number(serv.precio || serv.Precio), cantidad: 1 });

        // Si ya está seleccionado "Pagar Ahora" y es el primer item, mostrar modal
        if (this.tipoVenta === 'pagar_ahora' && this.carrito.length === 1) {
          this.mostrarModalFactura = true;
        }
      }
    },
    eliminarServicio(index) {
      this.carrito.splice(index, 1);
    },
    async buscarReservas() {
      if (this.busquedaReserva.length < 2) return;
      try {
        const res = await axios.get(`${API_URL}/reservas/buscar-avanzado`, {
          params: { q: this.busquedaReserva }
        });
        this.reservasFiltradas = Array.isArray(res.data.data) ? res.data.data : [];
      } catch (err) {
        console.error('Error buscando reservas:', err);
        this.reservasFiltradas = [];
      }
    },
    async obtenerMetodosPago(conFactura) {
      try {
        const endpoint = conFactura
          ? `${API_URL}/metodos-pago/facturados`
          : `${API_URL}/metodos-pago/no-facturados`;
        const res = await axios.get(endpoint);
        this.metodosPago = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error('Error al obtener métodos de pago:', error);
      }
    },
    requiereReferencia(idMetodo) {
      return [2, 3, 4].includes(Number(idMetodo));
    },
    agregarPago() {
      this.pagos.push({ IdMetodoPago: '', Monto: '', NumeroReferencia: '', IdCuentaBancaria: '' });
    },
    removerPago(index) {
      if (this.pagos.length > 1) {
        this.pagos.splice(index, 1);
      }
    },
    onMetodoPagoChange(index) {
      // Reset campos cuando cambia el método
      this.pagos[index].IdCuentaBancaria = '';
      this.pagos[index].NumeroReferencia = '';
    },
    seleccionarHabitacion(reserva, habitacion) {
      this.reservaSeleccionada = reserva;
      this.habitacionSeleccionada = habitacion;
      this.reservasFiltradas = [];
    },
    limpiarSeleccionReserva() {
      this.reservaSeleccionada = null;
      this.habitacionSeleccionada = null;
      this.busquedaReserva = '';
    },
    // Flujo de venta
    iniciarVenta() {
      if (this.tipoVenta === 'pagar_ahora') {
        this.mostrarModalFactura = true;
      } else if (this.tipoVenta === 'cargar_reserva') {
        this.finalizarVentaReserva();
      }
    },
    async seleccionarTipoFactura(tipo) {
      this.tipoFacturacion = tipo;
      this.mostrarModalFactura = false;

      // Cargar métodos de pago según tipo
      const conFactura = tipo === 'con_factura';
      await this.obtenerMetodosPago(conFactura);

      // Resetear pagos y subTipoFactura
      this.subTipoFactura = null;
      this.pagos = [{ IdMetodoPago: '', Monto: this.totalVenta, NumeroReferencia: '', IdCuentaBancaria: '' }];

      this.mostrarModalPago = true;
    },
    cerrarModalFactura() {
      this.mostrarModalFactura = false;
      // Resetear tipo de venta si se cierra el modal
      if (this.tipoVenta === 'pagar_ahora') {
        this.tipoVenta = null;
      }
    },
    cerrarModalPago() {
      this.mostrarModalPago = false;
      this.tipoFacturacion = null;
      this.subTipoFactura = null;
      this.nitCliente = '';
      this.nombreCliente = '';
      this.direccionReceptor = '';
      this.nitVerificado = false;
      this.otroServicio = false;
      this.pagos = [{ IdMetodoPago: '', Monto: '', NumeroReferencia: '', IdCuentaBancaria: '' }];
    },
    async finalizarVenta() {
      try {
        this.cargando = true;

        const esCF = this.tipoFacturacion === 'sin_factura' || this.subTipoFactura === 'cf';
        // Calcular cambio antes de modificar pagos
        const _cambio = Math.max(0, this.totalPagado - this.totalVenta);
        // Distribuir cambio: descontar del primer pago efectivo
        let _cambioRestante = _cambio;
        const payload = {
          Total: this.totalVenta,
          IdReservaHabitacion: null,
          IdEmpleado: this.getUser?.IdEmpleado || null,
          Facturado: this.facturadoValue,
          generar_factura: this.tipoFacturacion === 'con_factura',
          otro_servicio: this.otroServicio ? 1 : 0,
          detalles: this.carrito.map(s => ({ IdServicio: s.IdServicio, Cantidad: s.cantidad })),
          pagos: this.pagos.filter(p => p.IdMetodoPago && p.Monto > 0).map(p => {
            const m = this.metodosPago.find(mp => mp.IdMetodoPago == p.IdMetodoPago);
            const esEfect = m?.Metodo?.toLowerCase().includes('efectivo') ?? false;
            let monto = Number(p.Monto);
            if (esEfect && _cambioRestante > 0) {
              const desc = Math.min(_cambioRestante, monto);
              monto -= desc;
              _cambioRestante -= desc;
            }
            return {
              Monto: monto,
              IdMetodoPago: p.IdMetodoPago,
              IdCuentaBancaria: p.IdCuentaBancaria || null,
              NumeroReferencia: this.requiereReferencia(p.IdMetodoPago) ? p.NumeroReferencia : null,
            };
          }).filter(p => p.Monto > 0)
        };

        if (this.tipoFacturacion === 'con_factura') {
          payload.receptor_nit    = esCF ? 'CF' : (this.nitCliente.trim() || 'CF');
          payload.receptor_nombre = esCF ? 'Consumidor Final' : (this.nombreCliente.trim() || 'Consumidor Final');
          payload.receptor_direccion = this.direccionReceptor.trim() || null;
        }

        const { data } = await axios.post(`${API_URL}/venta-servicios-extra`, payload);
        this.mostrarToastMensaje('Venta registrada correctamente');

        if (data.factura?.uuid) {
          this.esperandoImpresion = true;
          setTimeout(() => this.imprimirTicketTermica(data.factura.uuid, _cambio), 900);
        }

        this.limpiarFormulario();
        this.cerrarModalPago();

      } catch (error) {
        console.error(error.response?.data || error);
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al registrar la venta.', 'danger');
      } finally {
        this.cargando = false;
      }
    },
    async finalizarVentaReserva() {
      try {
        this.cargando = true;

        const payload = {
          Total: this.totalVenta,
          IdReservaHabitacion: this.habitacionSeleccionada?.IdReservaHabitacion,
          IdEmpleado: this.getUser?.IdEmpleado || null,
          Facturado: 2,
          detalles: this.carrito.map(s => ({ IdServicio: s.IdServicio, Cantidad: s.cantidad })),
          pagos: []
        };

        await axios.post(`${API_URL}/venta-servicios-extra`, payload);
        this.mostrarToastMensaje('Venta cargada a la habitación correctamente');

        this.limpiarFormulario();

      } catch (error) {
        console.error(error.response?.data || error);
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al registrar la venta.', 'danger');
      } finally {
        this.cargando = false;
      }
    },
    limpiarFormulario() {
      this.carrito = [];
      this.tipoVenta = null;
      this.tipoFacturacion = null;
      this.subTipoFactura = null;
      this.nitCliente = '';
      this.nombreCliente = '';
      this.direccionReceptor = '';
      this.pagos = [{ IdMetodoPago: '', Monto: '', NumeroReferencia: '', IdCuentaBancaria: '' }];
      this.limpiarSeleccionReserva();
      this.busqueda = '';
      this.serviciosFiltradosResultado = [];
    },
    mostrarToastMensaje(mensaje, tipo = 'success') {
      this.alerta.mensaje = mensaje;
      this.alerta.tipo = tipo;
      this.mostrarToast = true;
      setTimeout(() => this.mostrarToast = false, 3000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    }
  },
  created() {
    this.debouncedBuscarReservas = debounce(this.buscarReservas, 400);
  },
  mounted() {
    this.obtenerCuentasBancarias();
    this.cargarTodosLosServicios();
  }
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.modal {
  display: none;
  z-index: 1050;
}
.modal.show {
  display: block;
}
</style>
